import { Region } from '@/services/auth/types';
import { useAnalytics } from '@/services/analytics';
import { BOOT_START } from '@/services/performance/boot-start';
import { useStore } from '@nanostores/react';
import { createContext, use, useEffect, useRef, useState } from 'react';
import { accountStore } from '@/services/auth/store';

export const PerformanceMetric = {
  BootStart: 'bootStart',
  Authenticated: 'authenticated',
  ChildAppLoadStart: 'childAppLoadStart',
} as const;

export type PerformanceMetric = (typeof PerformanceMetric)[keyof typeof PerformanceMetric];

type Metrics = Record<PerformanceMetric, number | null>;
type RecordMetric = (metric: PerformanceMetric) => void;

// Due to the way analytics tracking is implemented, the performance provider
// can only be rendered on the authenticated pages. Until we support anonymous
// events, we'll provide a default empty function for the context rather than
// throw an error when context is not defined.
// oxlint-disable-next-line typescript/no-empty-function
const PerformanceContext = createContext<RecordMetric>(() => {});

export function usePerformance() {
  return use(PerformanceContext);
}

type PerformanceProviderProps = { children: React.ReactNode };

export function PerformanceProvider({ children }: PerformanceProviderProps) {
  const { track } = useAnalytics();
  const account = useStore(accountStore);
  const isSending = useRef(false);
  const hasBeenSent = useRef(false);
  const [hasTriedToOverrideMetric, setHasTriedToOverrideMetric] = useState(false);
  const [metrics, setMetrics] = useState<Metrics>({
    [PerformanceMetric.BootStart]: BOOT_START,
    [PerformanceMetric.Authenticated]: null,
    [PerformanceMetric.ChildAppLoadStart]: null,
  });

  useEffect(() => {
    if (Object.values(metrics).includes(null) || hasBeenSent.current || isSending.current) {
      return;
    }

    isSending.current = true;

    const calculatedMetrics = {
      timeToAuthenticated: (metrics.authenticated ?? 0) - (metrics.bootStart ?? 0),
      timeToChildAppLoadStart: (metrics.childAppLoadStart ?? 0) - (metrics.bootStart ?? 0),
    };

    void track('performance', 'hydraInitialLoad', {
      'Account Id': account?.id,
      'Data Center': account?.region === Region.EU ? 'eu' : 'us',
      'Route Name': window.location.pathname,
      'Has Tried To Override Metric': hasTriedToOverrideMetric,
      'Hydra Boot Start': metrics.bootStart ?? 0,
      'Hydra Authenticated': metrics.authenticated ?? 0,
      'Hydra Child App Load Start': metrics.childAppLoadStart ?? 0,
      'Hydra Time To Authenticated': calculatedMetrics.timeToAuthenticated ?? 0,
      'Hydra Time To Child App Load Start': calculatedMetrics.timeToChildAppLoadStart ?? 0,
    }).finally(() => {
      hasBeenSent.current = true;
    });
  }, [hasBeenSent, isSending, metrics, account, hasTriedToOverrideMetric, track]);

  function saveMetric(metric: PerformanceMetric) {
    // If this metric has alreavy been set, ignore it
    if (metrics[metric] != null) {
      setHasTriedToOverrideMetric(true);
      return;
    }

    setMetrics((previousMetrics) => ({
      ...previousMetrics,
      [metric]: performance.now(),
    }));
  }

  return <PerformanceContext.Provider value={saveMetric}>{children}</PerformanceContext.Provider>;
}
